<template>
  <div :id="iuid" class="input-dropdown" :class="{ primary, secondary, tertiary, fill, disabled }">
      <label v-if="label || forReport || forState">
        <span v-if="label" v-html="label" />
        <Icon v-if="forReport" name="fieldsSAV" />
        <Icon v-if="forState" name="fieldsState" />
        <Tooltip v-if="tooltip" icon="info" :content="tooltip" />
      </label>
      <div tabindex="0" @focusin="handleFocusIn" @focusout="handleFocusOut">
        <div>
          <span class="placeholder" v-html="computedPlaceholder" />
          <Icon name="chevronDown" /><Icon name="chevronUp" />
        </div>
        <ul ref="options">
          <li v-if="searchable" ref="search" class="search">
            <input
              type="text"
              placeholder="Suche"
              v-model="searchValue"
              @keydown="handleKeyboardEvents($event, true)"
            />
          </li>
          <template v-if="multiselect">
            <li
              class="multi-select"
              v-for="(item, idx) in computedOptions"
              :key="`${iuid}-item-${idx}`"
              :data-index="idx">
              <Checkbox
                ref="checkboxes"
                :label="item.label"
                :value="item.value"
                :checked.sync="ivalue"
                :tabindex="-1"
              />
            </li>
          </template>
          <template v-else>
            <li
              v-for="(item, idx) in computedOptions"
              :key="`${iuid}-item-${idx}`"
              :data-index="idx"
              :class="{ selected: !actionOnly && ivalue === item.value }"
              @click="handleItemClick(item.value)">
                <Icon v-if="!actionOnly && ivalue === item.value" name="checkmark" />
                <Icon v-if="item.icon" :name="item.icon" />
                <span v-html="item.label" />
            </li>
          </template>
        </ul>
      </div>
  </div>
</template>

<script>
  import DomEvents from '@/objects/DomEvents';
  import Keys from '@/objects/KeyboardKeys';
  import Icon from '@/components/ui/Icon';
  import TextInput from '@/components/ui/inputs/TextInput';
  import Checkbox from '@/components/ui/inputs/Checkbox';
  import Tooltip from '@/components/ui/Tooltip';

  let uid = 0;

  export default {
    name: 'Dropdown',
    components: {
      Tooltip,
      Checkbox,
      TextInput,
      Icon,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      label: {
        type: String,
        default: null,
      },
      placeholder: {
        type: String,
        default: null,
      },
      placeholderPrefix: {
        type: String,
        default: null,
      },
      tooltip: {
        type: String,
        default: null,
      },
      options: {
        type: Array,
        default: () => [],
      },
      value: {
        type: [String, Array, Number],
        default: null,
      },
      //----------------------------------
      // Options
      //----------------------------------
      actionOnly: {
        type: Boolean,
        default: false,
      },
      staticPlaceholder: {
        type: Boolean,
        default: false,
      },
      toggleable: {
        type: Boolean,
        default: false,
      },
      searchable: {
        type: Boolean,
        default: false,
      },
      multiselect: {
        type: Boolean,
        default: false,
      },
      forState: {
        type: Boolean,
        default: false,
      },
      forReport: {
        type: Boolean,
        default: false,
      },
      vuelidate: {
        type: Object,
        default: null,
      },
      //----------------------------------
      // CSS Related Properties
      //----------------------------------
      primary: {
        type: Boolean,
        default: false,
      },
      secondary: {
        type: Boolean,
        default: false,
      },
      tertiary: {
        type: Boolean,
        default: false,
      },
      fill: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      uid += 1;
      return {
        iuid: `input-dropdown-${uid}`,
        hasBoundEvents: false,
        activeElement: null,
        clickTimeout: null,
        searchValue: null,
        ivalue: (this.multiselect) ? this.value || [] : this.value || null,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      computedPlaceholder() {
        const value = this.ivalue;
        const labels = this.options.filter((o) => {
          if (this.multiselect) {
            return value.includes(o.value);
          }
          return o.value === value;
        }).map(o => o.label);

        let processedLabel = (labels.length !== 0) ? labels.join(', ') : null;
        if (labels.length > 2) {
          const spliced = labels.splice(0, 2);
          processedLabel = `${spliced.join(', ')}, (+${labels.length})`;
        }

        const label = (this.staticPlaceholder || !processedLabel) ? this.placeholder : processedLabel;
        const prefix = this.placeholderPrefix;
        return (prefix) ? `<span class="prefix">${prefix}</span> ${label || ''}` : label;
      },
      computedOptions() {
        const search = this.searchValue;
        if (search !== null && /\S/.test(search)) {
          return this.options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
        }
        return this.options;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      activeElement(newNode, oldNode) {
        const CSS_SELECTED = 'selected';
        if (oldNode) { oldNode.classList.remove(CSS_SELECTED); }
        if (newNode) { newNode.classList.add(CSS_SELECTED); }
      },
      ivalue(value) {
        if (!this.actionOnly) {
          this.$emit('update:value', value);
        }
        this.$emit('input', value);
        if (this.actionOnly) {
          this.$nextTick(() => {
            this.ivalue = null;
          });
        }
      },
      value(value) {
        this.ivalue = value;
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    mounted() {
      if (this.searchable) {
        this.$refs.options.addEventListener(DomEvents.SCROLL, this.handleScroll);
      }
    },
    // beforeUpdate() {},
    // updated() {},
    beforeDestroy() {
      if (this.searchable) {
        this.$refs.options.removeEventListener(DomEvents.SCROLL, this.handleScroll);
      }
    },
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      up() {
        const { options } = this.$refs;
        let activeElement = this.activeElement || options.firstElementChild;
        const previousElement = activeElement.previousElementSibling;
        activeElement = previousElement || options.lastElementChild;
        if (this.searchable && activeElement.classList.contains('search')) {
          this.activeElement = activeElement;
          this.up();
        } else {
          this.focus(activeElement);
        }
      },
      down() {
        const { options } = this.$refs;
        let activeElement = this.activeElement || options.lastElementChild;
        const nextElement = activeElement.nextElementSibling;
        activeElement = nextElement || options.firstElementChild;
        if (this.searchable && activeElement.classList.contains('search')) {
          this.activeElement = activeElement;
          this.down();
        } else {
          this.focus(activeElement);
        }
      },
      select() {
        const activeElement = this.activeElement;
        if (activeElement && activeElement.dataset.index) {
          const idx = parseFloat(activeElement.dataset.index);
          if (this.multiselect) {
            const checkbox = this.$refs.checkboxes[idx];
            if (checkbox) {
              checkbox.toggle();
            }
          } else {
            this.handleItemClick((this.options[idx] || {}).value);
            this.handleFocusOut();
          }
        }
      },
      focus(node) {
        this.activeElement = node;
        node.scrollIntoView({ block: 'center' });
      },
      close() {
        this.$nextTick(() => {
          this.handleCloseClick();
        });
      },

      attachEventHandlers() {
        window.addEventListener(DomEvents.KEY_DOWN, this.handleKeyboardEvents);
        this.clickTimeout = setTimeout(() => {
          this.$el.addEventListener(DomEvents.CLICK, this.handleCloseClick);
        }, 250);
        this.hasBoundEvents = true;
      },
      detachEventHandlers() {
        clearInterval(this.clickTimeout);
        window.removeEventListener(DomEvents.KEY_DOWN, this.handleKeyboardEvents);
        this.$el.removeEventListener(DomEvents.CLICK, this.handleCloseClick);
        this.hasBoundEvents = false;
        this.activeElement = null;
      },

      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleFocusIn() {
        if (!this.hasBoundEvents) {
          this.attachEventHandlers();
          const { options } = this.$refs;
          const optionsRect = options.getBoundingClientRect();
          const parentRect = this.$el.getBoundingClientRect();
          const x = (parentRect.x + parentRect.width) - optionsRect.width;
          const maxY = (parentRect.y + parentRect.height) + optionsRect.height;
          options.className = null;
          if (x < 10) {
            options.classList.add('left');
          }
          if (maxY > (window.innerHeight - 10)) {
            options.classList.add('top');
          }
        }
      },
      handleFocusOut(evt = null) {
        if (evt === null || (evt && !this.$el.contains(evt.relatedTarget))) {
          this.detachEventHandlers();
          this.$el.blur();
          this.searchValue = null;
        }
      },
      handleCloseClick() {
        this.handleFocusOut();
      },
      handleItemClick(value) {
        if (this.toggleable && value === this.ivalue) {
          // eslint-disable-next-line no-param-reassign
          value = null;
        }
        this.ivalue = value;
        if (value === null) {
          this.$emit('input', value);
        }
      },
      handleKeyboardEvents(evt, searchboxEvent = false) {
        if (searchboxEvent && [Keys.ARROW_UP, Keys.ARROW_UP_ALT, Keys.ARROW_DOWN, Keys.ARROW_DOWN_ALT].includes(evt.key)) {
          evt.preventDefault();
          evt.stopPropagation();
        }
        switch (evt.key) {
          case Keys.ARROW_UP:
          case Keys.ARROW_UP_ALT:
            this.up();
            break;
          case Keys.ARROW_DOWN:
          case Keys.ARROW_DOWN_ALT:
            this.down();
            break;
          case Keys.ESCAPE:
          case Keys.ESCAPE_ALT:
            this.handleFocusOut();
            break;
          case Keys.ENTER:
          case Keys.SPACE:
            this.select();
            break;
          default:
            break;
        }
      },
      handleScroll() {
        const CSS_SCROLLED = 'scrolled';
        const { options, search } = this.$refs;
        const scrollTop = options?.scrollTop;
        if (scrollTop && scrollTop > 8) {
          search.classList.add(CSS_SCROLLED);
        } else {
          search.classList.remove(CSS_SCROLLED);
        }
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.input-dropdown {
  position: relative;
  display: inline-block;
  height: 48px;
  border-radius: $default-border-radius;
  outline: 0;

  & > label {
    display: block;
    @include font-body-medium;
    color: $color-black-solid-100;
    margin-bottom: 4px;

    & > svg {
      position: relative;
      top: 2px;
      width: 14px;
      height: 14px;
      margin-left: 4px;
    }

    & > .comp-tooltip {
      margin-left: 8px;
    }
  }

  & > div {
    position: relative;
    display: inline-block;
    height: 48px;
    border-radius: $default-border-radius;
    outline: 0;

    & > div {
      display: inline-flex;
      align-items: center;
      padding: 16px 22px;
      height: 48px;
      cursor: pointer;
      user-select: none;

      span.placeholder {
        @include font-body-small;
        overflow: hidden;
        text-overflow: ellipsis;
        color: currentColor;

        span.prefix {
          @include font-body-small;
          color: $color-black-alpha-60;
        }
      }
      svg {
        margin-left: 8px;
        width: 14px;
        height: 14px;

        &:first-of-type { display: inline-block; }
        &:last-of-type { display: none; }
      }
      span:empty {
        height: 8px;
        & ~ svg { margin-left: 0; }
      }
    }

    & > ul {
      position: absolute;
      top: calc(100% + 4px);
      right: 0;
      margin: 0;
      padding: 0 0;
      list-style-type: none;
      width: inherit;
      min-width: 184px;
      max-height: calc(32vh);
      background-color: $color-white-solid-10;
      border-radius: $default-border-radius;
      box-shadow: $box-shadow-5;
      @include font-body-small;
      color: $color-black-solid-100;
      pointer-events: none;
      opacity: 0;
      will-change: opacity;
      transition: opacity 0.15s ease;
      overflow-x: hidden;
      overflow-y: auto;
      z-index: 5;

      &.left { left: 0; right: auto; }
      &.top {
        bottom: calc(100% + 4px);
        top: auto;
      }

      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      &::-webkit-scrollbar-track {
        border: 0;
        outline: 0;
      }
      &::-webkit-scrollbar-thumb {
        background-color: $color-blue-solid-100;
        border: 2px solid $color-white-solid-10;
        border-radius: 4px;
        outline: 0;
      }

      li {
        position: relative;
        padding: 13px 14px;
        cursor: pointer;
        z-index: 1;

        &:before {
          position: absolute;
          content: '';
          left: 4px;
          top: 2px;
          width: calc(100% - 8px);
          height: calc(100% - 4px);
          background-color: transparent;
          will-change: background-color;
          transition: background-color 0.2s linear;
          border-radius: $default-border-radius;
          z-index: -1;
        }

        & > svg {
          position: relative;
          top: 2px;
          width: 14px;
          height: auto;

          &:nth-child(2) {
            margin-left: 8px;
          }
          & + span {
            margin-left: 8px;
          }
        }

        &.selected {
          @include font-body-small-bold;
        }

        &.search {
          background-color: $color-white-solid-10;
          position: sticky;
          top: 0;
          margin-top: 0;
          z-index: 2;
          box-shadow: $box-shadow-0;
          padding: 6px 6px 6px 8px;
          transition: box-shadow $default-transition;

          &:before {
            display: none;
          }

          &.scrolled {
            box-shadow: $box-shadow-2;
          }

          &:hover, &.selected, &.selected:hover {
            background-color: $color-white-solid-10 !important;
          }

          input[type=text] {
            outline: 0;
            @include font-body-small;
            padding: 11px 7px;
            width: 100%;
            border-radius: 4px;
            border: 1px solid $color-blue-alpha-80;
          }
        }

        &.multi-select {
          padding: 0;
          margin: 0;

          .input-checkbox {
            width: 100%;
            padding: 0;
            height: 42px;

            input[type=checkbox] {
              & + label {
                height: 42px;
                width: 100%;
                display: inline-flex;
                align-items: center;
                padding: 13px 14px 13px 38px;
                @include font-body-small;
              }

              /* Box */
              & + label:before {
                top: 12px;
                left: 14px;
              }

              /* Checkmark */
              & + label:after {
                left: 19px;
                top: 15px;
              }
            }
          }
        }
      }
    }

    &:focus, &:focus-within {
      & > ul {
        opacity: 1;
        pointer-events: all;
      }

      & > div > svg {
        &:first-of-type { display: none; }
        &:last-of-type { display: inline-block; }
      }
    }
  }

  &.primary {
    & > div {
      background-color: $color-blue-solid-100;
      color: $color-white-solid-10;
      & > ul li.selected, & > ul li:hover {
        &:before {
          background-color: $color-black-alpha-05;
        }
      }
    }
  }

  &.secondary {
    & > div {
      background-color: $color-white-solid-10;
      color: $color-black-solid-100;
      & > ul li.selected, & > ul li:hover {
        &:before {
          background-color: $color-black-alpha-05;
        }
      }
    }
  }

  &.tertiary {
    & > div {
      background-color: $color-black-alpha-10;
      color: $color-black-solid-100;
      & > ul li.selected, & > ul li:hover {
        &:before {
          background-color: $color-black-alpha-05;
        }
      }
    }
  }

  &.fill {
    & > div {
      display: block;
      & > div {
        display: flex;
        justify-content: space-between;
      }
    }
  }

  &.disabled {
    pointer-events: none;
    & > div {
      opacity: 0.5;
    }
  }
}
</style>
